import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { SectionTitle, StatusChip } from "@/components/common";
import { Sun, Moon, Monitor, Type, Zap, Bell, User, KeyRound, Info, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const APP_VERSION = "Shadow Guide v1.1 · Phase 1";

function Toggle({ label, desc, active, onToggle, testid, icon: Icon }) {
  return (
    <button onClick={onToggle} data-testid={testid}
      className="w-full flex items-center justify-between sg-card-elev p-4 text-left">
      <div className="flex items-center gap-3">
        {Icon && <Icon size={18} style={{ color: "var(--accent)" }} />}
        <div><div className="font-semibold text-sm">{label}</div>{desc && <div className="text-xs" style={{ color: "var(--txt2)" }}>{desc}</div>}</div>
      </div>
      <div className="w-12 h-7 rounded-full p-1 transition-colors" style={{ background: active ? "var(--accent)" : "var(--brd)" }}>
        <div className="w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: active ? "translateX(20px)" : "none" }} />
      </div>
    </button>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { prefs, update } = useTheme();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const canAdvanced = user.role === "owner" || user.role === "manager";

  const themes = [["system", "System", Monitor], ["light", "Light", Sun], ["dark", "Dark", Moon]];

  const changePassword = async () => {
    try {
      await api.post("/me/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl">
      <SectionTitle sub="Your account & app preferences" right={
        canAdvanced && <button className="sg-btn" onClick={() => navigate("/settings/advanced")} data-testid="open-advanced"><SlidersHorizontal size={16} /> Advanced Settings</button>
      }>Settings</SectionTitle>

      <div className="sg-card p-6 mb-6" data-testid="appearance-section">
        <div className="font-head text-xl font-bold mb-4">Appearance</div>
        <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Theme</label>
        <div className="grid grid-cols-3 gap-3 mt-2 mb-5">
          {themes.map(([key, label, Icon]) => (
            <button key={key} onClick={() => update({ theme: key })} data-testid={`theme-${key}`}
              className={`sg-card-elev p-4 flex flex-col items-center gap-2 ${prefs.theme === key ? "tile-active" : ""}`}>
              <Icon size={22} style={{ color: prefs.theme === key ? "var(--accent)" : "var(--txt2)" }} />
              <span className="font-semibold text-sm">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Text Size</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[["standard", "Standard"], ["large", "Large"]].map(([k, l]) => (
                <button key={k} onClick={() => update({ text_size: k })} data-testid={`text-${k}`}
                  className={`sg-card-elev p-3 flex items-center justify-center gap-2 ${prefs.text_size === k ? "tile-active" : ""}`}>
                  <Type size={16} /> {l}
                </button>
              ))}
            </div>
          </div>
          <Toggle label="Reduced Motion" desc="Minimize animations for field use" icon={Zap}
            active={prefs.reduced_motion} onToggle={() => update({ reduced_motion: !prefs.reduced_motion })} testid="toggle-motion" />
          <Toggle label="Sound / Feedback" desc="Audible & haptic confirmations where supported" icon={Bell}
            active={prefs.sound_feedback} onToggle={() => update({ sound_feedback: !prefs.sound_feedback })} testid="toggle-sound" />
        </div>
      </div>

      <div className="sg-card p-6 mb-6" data-testid="notifications-section">
        <div className="font-head text-xl font-bold mb-4">Notifications</div>
        <div className="flex flex-col gap-3">
          {[["equipment", "Equipment alerts"], ["qc", "QC & correction alerts"], ["approvals", "Approval requests"]].map(([k, l]) => (
            <Toggle key={k} label={l} active={!!prefs.notifications?.[k]} testid={`notif-${k}`}
              onToggle={() => update({ notifications: { ...prefs.notifications, [k]: !prefs.notifications?.[k] } })} />
          ))}
        </div>
        <label className="text-xs uppercase tracking-wider mt-5 block" style={{ color: "var(--txt2)" }}>Default landing screen</label>
        <select className="sg-input mt-2" value={prefs.default_landing} data-testid="default-landing" onChange={(e) => update({ default_landing: e.target.value })}>
          {["home", "today", "jobs", "estimator"].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      <div className="sg-card p-6 mb-6" data-testid="profile-section">
        <div className="font-head text-xl font-bold mb-4 flex items-center gap-2"><User size={18} style={{ color: "var(--accent)" }} /> My Profile</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
          <div><div className="text-xs" style={{ color: "var(--txt2)" }}>Name</div><div className="font-semibold">{user.name}</div></div>
          <div><div className="text-xs" style={{ color: "var(--txt2)" }}>Email</div><div className="font-semibold break-all">{user.email}</div></div>
          <div><div className="text-xs" style={{ color: "var(--txt2)" }}>Role</div><StatusChip status="certified" label={user.role} /></div>
        </div>
      </div>

      <div className="sg-card p-6 mb-6" data-testid="password-section">
        <div className="font-head text-xl font-bold mb-4 flex items-center gap-2"><KeyRound size={18} style={{ color: "var(--accent)" }} /> Change Password</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PasswordInput placeholder="Current password" value={pw.current_password} data-testid="pw-current" autoComplete="current-password" onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} />
          <PasswordInput placeholder="New password (min 8)" value={pw.new_password} data-testid="pw-new" autoComplete="new-password" onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} />
        </div>
        <button className="sg-btn sg-btn-accent mt-3" onClick={changePassword} data-testid="change-password-btn">Update Password</button>
      </div>

      <div className="sg-card p-5 flex items-center gap-3" data-testid="version-section">
        <Info size={18} style={{ color: "var(--txt2)" }} />
        <span className="text-sm" style={{ color: "var(--txt2)" }}>{APP_VERSION}</span>
      </div>
    </div>
  );
}
